import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { LinkClickedEvent } from '@atlassian/mpac-analytics';
import { BodyLarge, BodyNormal, H3, H5, LinkWithIcon } from '@atlassian/mpac-primitives';
import { css, styled } from '@compiled/react';
import React from 'react';

import { ANALYTICS_SUBJECT_ID_MEET_ROVO_SECTION_CTA } from '../../../../../analytics/Events';
import useCTAClickHandler from '../../../../../hooks/useCTAClickHandler';
import {
  MeetRovoSectionActionsConfig,
  MeetRovoSectionBannerConfig,
} from '../../config/meetRovoSection.config';

import CurvedArrow from '../../assets/productAssets/CurvedArrow.svg';
import RovoLogo from '../../assets/productAssets/RovoLogo.svg';

const meetRovoSectionContainerStyles = css({
  backgroundColor: token('elevation.surface.sunken'),
  maxWidth: '110rem',
  margin: 'auto',
});

const bannerContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'stretch',
  justifyContent: 'space-between',
  position: 'relative',

  '&::before': {
    content: '',
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    // could not find a design token for black background, so using a hex value
    backgroundColor: '#101214',
    clipPath: 'polygon(0 0, 100% 0, 100% 80%, 0 100%)',
    zIndex: '1',

    [media.below.md]: {
      clipPath: 'polygon(0 0, 100% 0, 100% 92%, 0 100%)',
    },
  },

  [media.above.lg]: {
    padding: `${token('space.800')} 136px 79px 136px`,
  },

  [media.above.sm]: {
    flexDirection: 'row',
    gap: `${token('space.300')}`,
    padding: `${token('space.500')} ${token('space.500')} 79px ${token('space.500')}`,
  },

  [media.below.sm]: {
    flexDirection: 'column-reverse',
    alignItems: 'center',
    gap: `${token('space.400')}`,
    padding: `${token('space.400')} ${token('space.300')} 79px ${token('space.300')}`,
  },
});

const bannerContentContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.300'),
  zIndex: '2',

  [media.above.md]: {
    maxWidth: '60%',
  },
});

const bannerContentLogoContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.100'),
});

const bannerContentTextStyles = css({
  marginTop: token('space.0'),
  color: token('color.text.inverse'),
});

const bannerContentTextContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.100'),
});

const bannerImageContainerStyles = css({
  [media.above.md]: {
    position: 'absolute',
    right: '10%',
    bottom: '8%',
  },
  [media.only.md]: {
    right: '2%',
  },
  [media.below.sm]: {
    transform: 'scale(0.95)',
  },
  zIndex: '2',
});

const actionsContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.500'),
  padding: `${token('space.800')} 136px ${token('space.1000')} 136px`,
  alignItems: 'center',
  alignSelf: 'stretch',

  [media.below.md]: {
    padding: `${token('space.500')} ${token('space.500')} ${token('space.1000')}`,
  },

  [media.below.sm]: {
    padding: `${token('space.500')} ${token('space.300')} 60px ${token('space.300')}`,
  },
});

const actionsCardContainerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: token('space.400'),
  alignSelf: 'stretch',
  [media.above.md]: {
    padding: `${token('space.0')} 6.250rem`,
  },

  [media.below.sm]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
});

const actionsContentContainerStyles = css({
  textAlign: 'center',
  [media.above.md]: {
    padding: `${token('space.0')} 6.250rem`,
  },
});

const actionsContentDescriptionStyles = css({
  lineHeight: '30px',
});

const actionsContentHeadingContainerStyles = css({
  display: 'flex',
  gap: token('space.100'),
  justifyContent: 'center',
});

const actionsContentHeadingStyles = css({
  position: 'relative',
});

const actionsContentHeadingArrowStyles = css({
  position: 'absolute',

  transform: 'scaleX(-1) scale(0.65)',
  bottom: '100%',
  left: '80%',

  [media.above.sm]: {
    transform: 'scale(0.65)',
    bottom: '10%',
    right: '100%',
    left: 'auto',
  },

  [media.above.md]: {
    transform: 'scaleX(1) scale(1)',
    bottom: '30%',
    right: '100%',
    left: 'auto',
  },
});

const actionsCardStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: '1 0 0',

  [media.below.sm]: {
    alignItems: 'center',
    textAlign: 'center',
  },
});

const actionsCardHeadingStyles = css({
  marginTop: token('space.200'),
});

const actionsCardDescriptionStyles = css({
  marginTop: token('space.100'),
});

const StyledLinkWithIcon = styled(LinkWithIcon)({
  color: token('color.text.inverse'),
  textDecoration: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '600',

  '&:hover, &:active': {
    color: token('color.text.inverse'),
  },
});

const BannerContainer = () => {
  return (
    <div css={bannerContainerStyles}>
      <BannerContentContainer />
      <BannerImageContainer />
    </div>
  );
};

const BannerContentContainer = () => {
  const handleCtaClick = useCTAClickHandler();

  return (
    <div css={bannerContentContainerStyles}>
      <div css={bannerContentLogoContainerStyles}>
        <img src={RovoLogo} alt="" />
        <BodyLarge css={bannerContentTextStyles}>Rovo</BodyLarge>
      </div>
      <div css={bannerContentTextContainerStyles}>
        <H3 css={bannerContentTextStyles}>{MeetRovoSectionBannerConfig.heading}</H3>
        <BodyLarge css={bannerContentTextStyles}>
          {MeetRovoSectionBannerConfig.description}
        </BodyLarge>
      </div>
      <StyledLinkWithIcon
        size="small"
        type="internal"
        href={MeetRovoSectionBannerConfig.ctaLink}
        animateIcon={true}
        underlineText={false}
        onClick={(event) =>
          handleCtaClick(
            event,
            LinkClickedEvent,
            ANALYTICS_SUBJECT_ID_MEET_ROVO_SECTION_CTA,
            {
              destinationUrl: MeetRovoSectionBannerConfig.ctaLink,
              linkText: MeetRovoSectionBannerConfig.ctaText,
            },
            MeetRovoSectionBannerConfig.ctaLink
          )
        }
        aria-label={MeetRovoSectionBannerConfig.ctaAriaLabel}
      >
        {MeetRovoSectionBannerConfig.ctaText}
      </StyledLinkWithIcon>
    </div>
  );
};

const BannerImageContainer = () => {
  return (
    <div css={bannerImageContainerStyles}>
      <img src={MeetRovoSectionBannerConfig.image} alt="" />
    </div>
  );
};

const ActionsContainer = () => {
  return (
    <div css={actionsContainerStyles}>
      <ActionsContentContainer />
      <ActionsCardContainer />
    </div>
  );
};

const ActionsContentContainer = () => {
  return (
    <div css={actionsContentContainerStyles}>
      <div css={actionsContentHeadingContainerStyles}>
        <H3 css={actionsContentHeadingStyles}>
          <img css={actionsContentHeadingArrowStyles} src={CurvedArrow} alt="" />
          {MeetRovoSectionActionsConfig.heading}
        </H3>
      </div>
      <BodyLarge css={actionsContentDescriptionStyles}>
        {MeetRovoSectionActionsConfig.description}
      </BodyLarge>
    </div>
  );
};

const ActionsCardContainer = () => {
  return (
    <div css={actionsCardContainerStyles}>
      {MeetRovoSectionActionsConfig.actions.map((card) => (
        <div css={actionsCardStyles} key={card.id}>
          <img src={card.logo} alt={card.description} />
          <H5 css={actionsCardHeadingStyles}>{card.heading}</H5>
          <BodyNormal css={actionsCardDescriptionStyles}>{card.description}</BodyNormal>
        </div>
      ))}
    </div>
  );
};

export const MeetRovoSection = () => {
  return (
    <div css={meetRovoSectionContainerStyles}>
      <BannerContainer />
      <ActionsContainer />
    </div>
  );
};
